// REQ-39 (Faz-7) — SMTP e-posta ayarları ekranı (settings.manage).
//
// Şifre write-only: form açıldığında alan HER ZAMAN boş gelir (GET'te "***"
// gelse bile prefill edilmez); boş gönderilirse BE mevcut şifreyi korur. Şifre
// düz metni yalnızca kaydetme anında istekle gider — state/log'da tutulmaz.

import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  Card,
  Col,
  Divider,
  Form,
  Input,
  InputNumber,
  Row,
  Skeleton,
  Space,
  Switch,
  Tag,
  Typography,
} from 'antd';
import { Mail, Save, Send, ShieldAlert } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import dayjs from 'dayjs';
import { PageHeader } from '@shared/components/PageHeader';
import { ROUTES } from '@shared/constants/routes';
import { useCan } from '@shared/rbac/useCan';
import { PERMISSIONS } from '@shared/rbac/permissions.const';
import { resolveApiErrorMessage } from '@/i18n/apiError';
import { smtpApi } from '@api/smtp';
import type { SmtpSettings } from '../types';

const { Text } = Typography;

interface FormValues {
  enabled: boolean;
  host: string;
  port: number;
  username: string;
  password: string;
  fromAddress: string;
  fromName: string;
  enableSsl: boolean;
  testRecipient: string;
}

function toFormValues(s: SmtpSettings): FormValues {
  return {
    enabled: s.enabled,
    host: s.host,
    port: s.port,
    username: s.username,
    password: '', // write-only — asla prefill edilmez
    fromAddress: s.fromAddress,
    fromName: s.fromName,
    enableSsl: s.enableSsl,
    testRecipient: '',
  };
}

export function SmtpSettingsPage() {
  const { t } = useTranslation(['smtp', 'common']);
  const canManage = useCan(PERMISSIONS.SETTINGS_MANAGE);
  const qc = useQueryClient();
  const [form] = Form.useForm<FormValues>();

  const { data, isLoading } = useQuery({
    queryKey: ['smtp-settings'],
    queryFn: () => smtpApi.get(),
    enabled: canManage,
  });

  // Sorgu geldiğinde formu doldur; şifre alanı boş kalır.
  useEffect(() => {
    if (data) form.setFieldsValue(toFormValues(data));
  }, [data, form]);

  const saveMutation = useMutation({
    mutationFn: (values: FormValues) =>
      smtpApi.update({
        host: values.host.trim(),
        port: values.port,
        username: values.username.trim(),
        // Boş → null (BE mevcut şifreyi korur).
        password: values.password.length > 0 ? values.password : null,
        fromAddress: values.fromAddress.trim(),
        fromName: values.fromName.trim(),
        enableSsl: values.enableSsl,
        enabled: values.enabled,
      }),
    onSuccess: (updated) => {
      qc.setQueryData(['smtp-settings'], updated);
      // Şifre alanını temizle — düz metni ekranda bırakma.
      form.setFieldValue('password', '');
      toast.success(t('toast.saved'));
    },
    onError: (err) => toast.error(resolveApiErrorMessage(err)),
  });

  const testMutation = useMutation({
    mutationFn: (recipient: string) => smtpApi.test(recipient.length > 0 ? recipient.trim() : null),
    onSuccess: (result) => {
      if (result.success) {
        toast.success(t('test.success'));
      } else {
        toast.error(
          result.error
            ? t('test.failure', { error: result.error })
            : t('test.failureNoDetail'),
        );
      }
    },
    onError: (err) => toast.error(resolveApiErrorMessage(err)),
  });

  const breadcrumb = [
    { label: t('common:labels.home'), to: ROUTES.DASHBOARD },
    { label: t('breadcrumb.settings') },
    { label: t('breadcrumb.smtp') },
  ];

  if (!canManage) {
    return (
      <div>
        <PageHeader title={t('page.title')} breadcrumb={breadcrumb} />
        <Card>
          <Alert
            type="error"
            showIcon
            icon={<ShieldAlert size={18} />}
            message={t('noAccess.title')}
            description={t('noAccess.description')}
          />
        </Card>
      </div>
    );
  }

  return (
    <div data-testid="smtp-settings-page">
      <PageHeader title={t('page.title')} description={t('page.description')} breadcrumb={breadcrumb} />

      <Card
        title={
          <Space>
            <Mail size={18} />
            {t('page.title')}
            {data ? (
              <>
                {data.enabled ? (
                  <Tag color="green">{t('status.enabled')}</Tag>
                ) : (
                  <Tag>{t('status.disabled')}</Tag>
                )}
                {data.hasPassword ? (
                  <Tag color="blue">{t('status.hasPassword')}</Tag>
                ) : (
                  <Tag color="orange">{t('status.noPassword')}</Tag>
                )}
              </>
            ) : null}
          </Space>
        }
        extra={
          data?.updatedAt ? (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {t('status.updatedAt', { value: dayjs(data.updatedAt).format('DD.MM.YYYY HH:mm') })}
            </Text>
          ) : null
        }
      >
        {isLoading ? (
          <Skeleton active paragraph={{ rows: 8 }} />
        ) : (
          <Form<FormValues>
            form={form}
            layout="vertical"
            initialValues={data ? toFormValues(data) : undefined}
            onFinish={(values) => saveMutation.mutate(values)}
          >
            <Form.Item name="enabled" label={t('fields.enabled.label')} valuePropName="checked" help={t('fields.enabled.help')}>
              <Switch />
            </Form.Item>

            <Row gutter={16}>
              <Col xs={24} md={16}>
                <Form.Item
                  name="host"
                  label={t('fields.host.label')}
                  rules={[{ required: true, message: t('fields.host.required') }]}
                >
                  <Input placeholder={t('fields.host.placeholder')} maxLength={255} />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item name="port" label={t('fields.port.label')}>
                  <InputNumber min={1} max={65535} precision={0} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item name="username" label={t('fields.username.label')}>
                  <Input placeholder={t('fields.username.placeholder')} maxLength={255} autoComplete="off" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="password" label={t('fields.password.label')} help={t('fields.password.help')}>
                  <Input.Password
                    placeholder={t('fields.password.placeholder')}
                    maxLength={255}
                    autoComplete="new-password"
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="fromAddress"
                  label={t('fields.fromAddress.label')}
                  rules={[{ type: 'email', message: t('fields.fromAddress.invalid') }]}
                >
                  <Input placeholder={t('fields.fromAddress.placeholder')} maxLength={255} />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="fromName" label={t('fields.fromName.label')}>
                  <Input placeholder={t('fields.fromName.placeholder')} maxLength={120} />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item name="enableSsl" label={t('fields.enableSsl.label')} valuePropName="checked">
              <Switch />
            </Form.Item>

            <Button
              type="primary"
              htmlType="submit"
              icon={<Save size={15} />}
              loading={saveMutation.isPending}
            >
              {t('save')}
            </Button>

            <Divider />

            <Text strong>{t('test.title')}</Text>
            <Row gutter={16} align="bottom" style={{ marginTop: 8 }}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="testRecipient"
                  label={t('test.recipientLabel')}
                  help={t('test.recipientHelp')}
                  rules={[{ type: 'email', message: t('fields.fromAddress.invalid') }]}
                  style={{ marginBottom: 8 }}
                >
                  <Input placeholder={t('test.recipientPlaceholder')} maxLength={255} />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item style={{ marginBottom: 8 }}>
                  <Button
                    icon={<Send size={15} />}
                    loading={testMutation.isPending}
                    onClick={() =>
                      testMutation.mutate(
                        (form.getFieldValue('testRecipient') as string | undefined) ?? '',
                      )
                    }
                  >
                    {t('test.button')}
                  </Button>
                </Form.Item>
              </Col>
            </Row>
          </Form>
        )}
      </Card>
    </div>
  );
}
